import { ReserveAccount } from '../api'
import { postV2ReserveAccount } from '../api-v2/helpers'
import { ReserveAccountFundsMapper } from '../api-v2/mappings'
import { CLog } from '../consoleMixin'
import { ApiVersionDelagator } from "./api-delegator"
/**
 * @typedef {import('../api-v2/js-doc').V1CreateReserveAccountRequest} V1CreateReserveAccountRequest
 * @typedef {import('../api-v2/js-doc').V2CreateReserveAccountRequest} V2CreateReserveAccountRequest
 * @typedef {import('../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 * @typedef {import('../api-v2/js-doc').V1ReserveAccountResponse} V1ReserveAccountResponse
 */

export class ReserveAccountApiDelegator extends ApiVersionDelagator {
  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveAccountResponse[]>}
   */
  async getReserveAccount (clientId) {
    return this.runGetApiCalls(() => {
      return this.#getReserveAccountV1(clientId)
    }, () => {
      return this.#getReserveAccountV2(clientId)
    })
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveAccountResponse[]>}
   */
  async #getReserveAccountV1 (clientId) {
    return (await ReserveAccount.queryList({
      client_id: clientId,
      sort_by: 'created_at',
      sort_direction: 'ASC',
    })).data.rows
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveAccountResponse[]>}
   */
  async #getReserveAccountV2 (clientId) {
    CLog('Fetching reserve account funds from', clientId)
    return []
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1CreateReserveAccountRequest} v1Request - V1 create reserve account request
   * @returns {Promise<V1ReserveAccountResponse>}
   */
  async postReserveAccount (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#postReserveAccountV1,
      this.#postReserveAccountV2.bind(this),
      {
        operationName: 'post-reserve-account',
      }
    )
  }

  /**
   * @param {V1CreateReserveAccountRequest} v1Request - V1 create reserve account request
   * @returns {Promise<V1ReserveAccountResponse>}
   */
  async #postReserveAccountV1 (v1Request) {
    return (await ReserveAccount.create(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1CreateReserveAccountRequest} v1Request - V1 create request
   * @param {V1ReserveAccountResponse=} v1Response - V1 create response
   * @returns {Promise<V1ReserveAccountResponse>}
   */
  async #postReserveAccountV2 (v2MappingOptions, v1Request, v1Response) {
    const v2Request = ReserveAccountFundsMapper.v1ToCreateRequest(v1Request, v1Response, v2MappingOptions.ingestThrough)
    const result = await postV2ReserveAccount(v1Request.client_id, v2Request)
    if (!await this.isV2ListingEnabled()) {
      return (await ReserveAccount.get(result.data.id)).data
    }
    return ReserveAccountFundsMapper.v2ReserveAccountFundsToV1(result.data)
  }
}
